import * as React from "react";
import cx from "classnames";
import { Flex } from "@common/components/Flex";
import { Hint } from "@common/components/hint";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";

import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { MovableItem } from "../movable-item";
import { TableFieldProps, useTableFieldController } from "./table-field.controller";
import { HeaderColumn } from "./components/header/column";

import "./table-field.styles.scss";

export const TableField: React.FC<TableFieldProps> = React.memo((props) => {
  const { datasheetElement, multipleElementsSelected, isTabOrderSettingActive } = props;

  const containerRef = React.useRef<HTMLDivElement>(null);

  const {
    coordinates,
    datasheetElementSelected,
    handleDatasheetElementMouseDown,
    movableElementRef,
    handleResizing,
    size,
    headingReference,
    headingPlacedHorizontally,
    headingSize,
    headingPosition,
    handleResizeColumn,
  } = useTableFieldController(props);

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext}
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
    />
  );

  return (
    <DatasheetItem
      style={{
        top:
          coordinates.y -
          (datasheetElement.original.headpos === DatasheetFieldComponentHeadingPosition.top ? headingSize.height : 0),
        left:
          coordinates.x -
          (datasheetElement.original.headpos === DatasheetFieldComponentHeadingPosition.left ? headingSize.width : 0),
        display: headingPlacedHorizontally ? "flex" : "block",
        alignItems: headingPlacedHorizontally ? "flex-start" : undefined,
      }}
    >
      {[DatasheetFieldComponentHeadingPosition.top, DatasheetFieldComponentHeadingPosition.left].includes(
        headingPosition
      ) && labelComponent}
      <Flex>
        <MovableItem
          resizing={{
            enabled: isTabOrderSettingActive ? false : !multipleElementsSelected,
            onChange: handleResizing,
          }}
          moving={{
            enabled: !isTabOrderSettingActive,
          }}
          showBorder={datasheetElement.original.showborder}
          reference={movableElementRef}
          height={size.height}
          width={size.width}
          selected={datasheetElementSelected}
          onMouseDown={isTabOrderSettingActive ? undefined : handleDatasheetElementMouseDown}
          className="DatasheetsEditorDatasheetTableField__movable-item"
        >
          <div 
            className={cx(
              "DatasheetsEditorDatasheetTableField__table", 
              { "DatasheetsEditorDatasheetTableField__table--disabled": isTabOrderSettingActive }
            )}
          >
            <Flex className="DatasheetsEditorDatasheetTableField__table__header" reference={containerRef}>
              {datasheetElement.original.field.subfields?.map((field) => (
                <HeaderColumn
                  // width={field.width}
                  width={100}
                  onResizeEnd={isTabOrderSettingActive ? undefined : handleResizeColumn}
                  title={field.fieldname}
                  containerRef={containerRef}
                  key={field.fieldid}
                  field={field}
                />
              ))}
            </Flex>
            <div className="DatasheetsEditorDatasheetTableField__table__body"></div>
          </div>
        </MovableItem>
        {datasheetElement.original.helptext && (
          <Hint className="DatasheetsEditorDatasheetTextField__hint" description="" />
        )}
      </Flex>
      {[DatasheetFieldComponentHeadingPosition.bottom, DatasheetFieldComponentHeadingPosition.right].includes(
        headingPosition
      ) && labelComponent}
    </DatasheetItem>
  );
});
